/**
 * Utility functions for modal interactions
 */

/**
 * Checks if form data has any filled fields (not empty strings, null, or default values)
 */
export const hasFormData = (formData: Record<string, any>, excludeFields?: string[]): boolean => {
  return Object.entries(formData).some(([key, value]) => {
    // Skip excluded fields (like IDs that are auto-filled)
    if (excludeFields?.includes(key)) return false;
    
    // Check if value is not empty/default
    if (typeof value === 'string') {
      // Skip default values like "Main Site"
      if (value === 'Main Site') return false;
      
      // For dates, check if they are default dates
      if (key === 'start_date' || key === 'end_date') {
        const today = new Date().toISOString().split('T')[0];
        if (value === today) return false;
        
        // Check if end_date is exactly 60 days from today (default)
        if (key === 'end_date') {
          const startDate = formData.start_date || today;
          const expectedEndDate = new Date(startDate);
          expectedEndDate.setDate(expectedEndDate.getDate() + 60);
          const expectedEndDateStr = expectedEndDate.toISOString().split('T')[0];
          if (value === expectedEndDateStr) return false;
        }
      }
      
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return value !== 0 && !Number.isNaN(value);
    }
    if (value instanceof Date) {
      return value.getTime() !== new Date().setHours(0, 0, 0, 0);
    }
    if (Array.isArray(value)) {
      // For arrays, check if they have meaningful content beyond defaults
      if (key === 'phases') {
        // Only consider phases as "filled" if there are more than the default "Planning" phase
        // or if the default phase has been modified
        return value.length > 1 || (value.length === 1 && value[0].name !== 'Planning');
      }
      return value.length > 0;
    }
    if (value && typeof value === 'object') {
      // Skip null objects (like address: null)
      if (value === null) return false;
      return Object.keys(value).length > 0;
    }
    
    // Handle null/undefined values
    if (value === null || value === undefined) return false;
    
    return true;
  });
};

/**
 * Creates a safe close handler that checks for unsaved changes
 */
export const createSafeCloseHandler = (
  formData: Record<string, any>,
  onClose: () => void,
  excludeFields?: string[]
) => {
  return (event?: React.MouseEvent) => {
    // Always allow closing via close button (no event means explicit close)
    if (!event) {
      onClose();
      return;
    }
    
    // Check if form has data
    if (hasFormData(formData, excludeFields)) {
      // Show confirmation before closing
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close this form?'
      );
      if (confirmed) {
        onClose();
      }
    } else {
      // No data, safe to close
      onClose();
    }
  };
};

/**
 * Checks if a date is not today (to exclude auto-filled today's date)
 */
export const isNotTodayDate = (dateString: string): boolean => {
  if (!dateString) return false;
  const inputDate = new Date(dateString).toDateString();
  const today = new Date().toDateString();
  return inputDate !== today;
}; 